/**
 * Copyright 2023 LiveKit, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  AudioTrack,
  LiveKitRoom,
  RoomAudioRenderer,
  TrackReference,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import EgressHelper from '@livekit/egress-sdk';
import { ConnectionState, RoomEvent, Track } from 'livekit-client';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import PrimaryHostLayout, { PrimaryHostDesktopLayout } from './PrimaryHostLayout';
import SingleSpeakerLayout from './SingleSpeakerLayout';
import SpeakerLayout from './SpeakerLayout';
import ParticipantTileWithFallback from './ParticipantTileWithFallback';
import { getPlaceholderUrl } from './url';

interface RoomPageProps {
  url: string;
  token: string;
  layout: string;
}

export default function RoomPage({ url, token, layout }: RoomPageProps) {
  const [error, setError] = useState<Error>();
  if (!url || !token) {
    return <div className="error">missing required params url and token</div>;
  }

  return (
    <LiveKitRoom serverUrl={url} token={token} onError={setError}>
      {error ? <div className="error">{error.message}</div> : <CompositeTemplate layout={layout} />}
    </LiveKitRoom>
  );
}

interface CompositeTemplateProps {
  layout: string;
}

function CompositeTemplate({ layout: initialLayout }: CompositeTemplateProps) {
  const room = useRoomContext();
  const items = initialLayout.split('|');
  const [layout, setLayout] = useState(initialLayout);
  const [hasScreenShare, setHasScreenShare] = useState(false);
  const [soloUserId, setSoloUserId] = useState(items.length === 2 ? items[1] : '');
  const screenshareTracks = useTracks([Track.Source.ScreenShare], {
    onlySubscribed: true,
  });
  const soloMode = soloUserId !== '' && layout.startsWith('single-speaker');
  const desktopMode = layout.startsWith('desktop');
  const roomContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (room) {
      EgressHelper.setRoom(room);

      // Egress layout can change on the fly, we can react to the new layout
      // here.
      EgressHelper.onLayoutChanged((newLayout) => {
        const items = newLayout.split('|');
        if (items.length === 1) {
          setLayout(newLayout);
        } else {
          setLayout(items[0]);
          setSoloUserId(items[1]);
        }
      });

      // start recording when there's already a track published
      let hasTrack = false;
      for (const p of Array.from(room.remoteParticipants.values())) {
        if (!soloUserId || (soloUserId && p.metadata && JSON.parse(p.metadata).user_id === soloUserId)) {
          if (p.trackPublications.size > 0) {
            hasTrack = true;
            break;
          }
        }
      }

      if (hasTrack) {
        EgressHelper.startRecording();
      } else {
        room.once(RoomEvent.TrackSubscribed, () => EgressHelper.startRecording());
      }
    }
  }, [room, layout, soloUserId]);

  useEffect(() => {
    if (screenshareTracks.length > 0 && screenshareTracks[0].publication) {
      setHasScreenShare(true);
    } else {
      setHasScreenShare(false);
    }
  }, [screenshareTracks]);

  const allTracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare, Track.Source.Unknown], {
    onlySubscribed: true,
  });
  const audioTracks = useTracks([Track.Source.Microphone], {
    onlySubscribed: true,
  });
  const filteredTracks = useMemo(() => {
    return allTracks.filter(
      (tr) =>
        tr.publication.kind === Track.Kind.Video &&
        (!soloUserId ||
          (soloUserId &&
            tr.participant.metadata &&
            String(JSON.parse(tr.participant.metadata).user_id) === soloUserId)) &&
        tr.participant.identity !== room.localParticipant.identity
    );
  }, [allTracks, soloUserId, room.localParticipant.identity]);

  const audioTracksWithoutVideo = useMemo(() => {
    return audioTracks.filter((track) => {
      return !filteredTracks.find(
        (ft) =>
          ft.participant.identity === track.participant.identity &&
          ft.participant.identity !== room.localParticipant.identity
      );
    });
  }, [audioTracks, filteredTracks, room.localParticipant.identity]);

  const combinedTracks: TrackReference[] = useMemo(() => {
    return [...filteredTracks, ...audioTracksWithoutVideo];
  }, [filteredTracks, audioTracksWithoutVideo]);

  const sortedTracks = useMemo(() => {
    return [...combinedTracks].sort((a, b) => {
      // sort by host first
      const isAHost = a.participant.metadata ? JSON.parse(a.participant.metadata)['isHost'] === true : false;
      const isBHost = b.participant.metadata ? JSON.parse(b.participant.metadata)['isHost'] === true : false;
      if (isAHost && !isBHost) {
        return -1;
      } else if (!isAHost && isBHost) {
        return 1;
      }
      return Number(a.participant.identity) - Number(b.participant.identity);
    });
  }, [combinedTracks]);

  const soloAudioMicReference = audioTracks.find(
    (tr) => soloUserId && sortedTracks.some((t) => t.participant.identity === tr.participant.identity)
  );

  let interfaceStyle = 'dark';
  if (layout.endsWith('-light')) {
    interfaceStyle = 'light';
  }

  let containerClass = 'roomContainer';
  if (interfaceStyle) {
    containerClass += ` ${interfaceStyle}`;
  }

  // determine layout to use
  let main: ReactElement = <></>;
  let effectiveLayout = layout;
  if (hasScreenShare && layout.startsWith('grid')) {
    effectiveLayout = layout.replace('grid', 'speaker');
  }

  // TODO: figure out if we want to use Egress.UpdateLayout to control this
  if (!effectiveLayout.startsWith('speaker') && !effectiveLayout.startsWith('all') && sortedTracks.length > 2) {
    effectiveLayout = 'primary-host';
  }

  useEffect(() => {
    sortedTracks.forEach((track, index) => {
      if (effectiveLayout !== 'primary-host' && track.participant.identity) {
        document.documentElement.style.setProperty(
          `--participant-background-image-${index + 1}`,
          getPlaceholderUrl(track.participant.identity)
        );
      }
    });
  }, [sortedTracks, effectiveLayout]);

  const sortedTracksForAllLayout = useMemo(() => {
    return [...sortedTracks].sort((a, b) => Number(a.participant.identity) - Number(b.participant.identity));
  }, [sortedTracks]);

  if (room.state !== ConnectionState.Disconnected) {
    if (desktopMode) {
      if (effectiveLayout.startsWith('primary-host')) {
        main = <PrimaryHostDesktopLayout tracks={sortedTracks} />;
      } else {
        main = (
          <div className={'desktop-layout-' + sortedTracks.length}>
            {sortedTracks.map((track, index) => (
              <ParticipantTileWithFallback key={index} trackRef={track} />
            ))}
          </div>
        );
      }
    } else if (effectiveLayout.startsWith('all')) {
      main = (
        <div className="all-layout">
          {sortedTracksForAllLayout.map((track, index) => (
            <ParticipantTileWithFallback key={index} trackRef={track} />
          ))}
        </div>
      );
    } else if (effectiveLayout.startsWith('speaker')) {
      main = <SpeakerLayout tracks={sortedTracks} />;
    } else if (effectiveLayout.startsWith('single-speaker')) {
      main = <SingleSpeakerLayout tracks={sortedTracks} />;
    } else if (effectiveLayout.startsWith('primary-host')) {
      main = <PrimaryHostLayout tracks={sortedTracks} />;
    } else {
      main = (
        <div className="lk-grid-layout">
          {sortedTracks.map((track, index) => (
            <ParticipantTileWithFallback key={index} trackRef={track} />
          ))}
        </div>
      );
    }
  }

  return (
    <div className={containerClass} ref={roomContainerRef}>
      {main}
      {/* when soloMode is true we never want to hear the room audio */}
      {soloMode ? soloAudioMicReference && <AudioTrack trackRef={soloAudioMicReference} /> : <RoomAudioRenderer />}
    </div>
  );
}
